import { useContext } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { UserModuleContext } from "../../context/UserModuleContext";
import { useAdInfo } from "./useAdInfo";

const WSU_RED = "#A60F2D";

// Used to move the chip label to a new line when the text is too long, so that the chip can wrap and not overflow its container.
const chipSx = {
    backgroundColor: "#fdf0f2",
    border: `1px solid ${WSU_RED}`,
    color: WSU_RED,
    fontWeight: 500,
    fontSize: "0.75rem",
    height: "auto",
    maxWidth: "100%",
    "& .MuiChip-label": {
        display: "block",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        py: 0.5,
    },
};

// AD stores certain timestamps (accountExpires, lastLogonTimestamp, pwdLastSet, etc.)
// as "FILETIME": 100-nanosecond intervals since 1601-01-01 UTC.
// 9223372036854775807 (Int64.MaxValue) is AD's sentinel for "never expires" / "never set" —
// it is not a real date and must never be passed to `new Date()` directly.
const AD_FILETIME_EPOCH_DIFF_MS = 11644473600000;
const AD_NEVER_EXPIRES_THRESHOLD = 1e18;
const AD_TIME_FIELD_PATTERN = /expir|lastlogon|pwdlastset/i;
const isAdTimeField = (key) => AD_TIME_FIELD_PATTERN.test(key);

const formatAdFileTime = (value) => {
    const num = typeof value === "string" ? Number(value) : value;
    if (num === null || num === undefined || Number.isNaN(num) || num === 0) return "Not set";
    if (num > AD_NEVER_EXPIRES_THRESHOLD) return "Never expires";
    const ms = num / 10000 - AD_FILETIME_EPOCH_DIFF_MS;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const renderCell = (key, value) => {
    if (isAdTimeField(key) && (typeof value === "number" || typeof value === "string")) {
        return <Typography variant="body2">{formatAdFileTime(value)}</Typography>;
    }

    if (Array.isArray(value)) {
        return value.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {value.map((v) => (
                    <Chip
                        key={v}
                        label={v}
                        size="small"
                        sx={chipSx}
                    />
                ))}
            </Box>
        ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">None</Typography>
        );
    }

    const display = typeof value === "boolean" ? value.toString() : value;
    return display !== null && display !== undefined && display !== "" ? (
        <Typography variant="body2">{display}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
};

const valueLength = (value) => {
    if (Array.isArray(value)) return value.join(", ").length;
    if (value === null || value === undefined) return 0;
    return String(value).length;
};

// Mirrors usePersonSections: one "tile" (currently just "Active Directory")
// carrying the length of its longest value so the grid can size it.
// Reads live from userModule.ADData (like usePersonSections reads
// userModule.userData) rather than useAdInfo's one-time fetch snapshot, so
// operations that patch ADData after the initial load (e.g. enabling or
// disabling the AD account) are reflected immediately.
export const useAdSections = () => {
    const { isLoaded, error } = useAdInfo();
    const { userModule } = useContext(UserModuleContext);

    if (!isLoaded || error) {
        return { isLoaded, error, tiles: [] };
    }

    const entries = Object.entries(userModule.ADData || {}).filter(
        ([key]) => key !== "statusCode" && key !== "DN"
    );

    const rows = entries.map(([key, value]) => ({
        key,
        label: key,
        content: renderCell(key, value),
        length: valueLength(value),
    }));

    const tiles = [{
        key: "Active Directory",
        heading: "Active Directory",
        rows,
        maxLength: rows.reduce((max, row) => Math.max(max, row.length), 0),
    }];

    return { isLoaded, error, tiles };
};
